import { css } from "styled-components";

// Constants
const BREAKPOINTS = {
    breakSmall: "320px",
    breakMobile: "375px",
    breakMobileMedium: "575px",
    breakTablet: "767px",
    breakOnlyMobile: "768px",
    breakMedium: "980px",
    breakIpadPro: "1024px",
    breakLarge: "1200px",
    breakBig: "1366px",
} as const;

const WIDTHS = {
    wide_450: "450px",
    wide_630: "630px",
    wide_650: "650px",
    wide_700: "700px",
    wide_768: "768px",
    wide_850: "850px",
    wide_1024: "1024px",
    wide_898: "894px",
    wide_750: "750px",
    wide_825: "825px",
    wide_900: "900px",
    wide_1104: "1104px",
    wide_1110: "1110px",
    wide_1150: "1150px",
} as const;

const SHARED_COLORS = {
    sub_2: "#C5C7C9",
    subtle: "#898D8F",
    muted: "#6E7375",
    toast_success: "#3ebd88",
    toast_info: "#3ebd88",
    toast_error: "#ff6961",
    slider_text: "#7257FF",
    gray: "#F6F6F6",
    gray_3: "#828282",
    gray_4: "#BDBDBD",
    gray_5: "#E0E0E0",
    gray_6: "#F2F2F2",
    gray_6_5: "#F3F3F3",
    white: "#FFFFFF",
    navy: "#1B2A41",
    green: "#09930B",
    purple: "#907AFF",
    commissionLabel: "#5336e2",
    commonTotal: "#31b4b9",
    commonAvail: "#147AD6",
} as const;

const BORDER_RADIUS = {
    small: "4px",
    medium: "8px",
    large: "16px",
    voucher: "50px",
} as const;

// Types
type ThemeColors = typeof SHARED_COLORS & {
    main: string;
    main_disable: string;
    label: string;
    hover: string;
    button_text: string;
    button_text1: string;
    bordered_button_text: string;
    bordered_button_color: string;
    highlight: string;
    border_chip: string;
    select: string;
    slider_text1: string;
    slider_text2: string;
    time_radio: string;
    points: string;
    points_text: string;
    share_border: string;
    share_background: string;
    items_background: string;
    items_color: string;
    gray_1: string;
    gray_2: string;
    gray_5_5: string;
    red: string;
    black: string;
    gold?: string;
    price?: string;
};

type ThemeGradients = {
    upgradeButton: string[];
    button: string[];
    buttonReversed: string[];
    bannerDiscount: string[];
    bannerPoints: string[];
    bannerCommission: string[];
    bannerPointsDesktop: string[];
    primary: string[];
    primaryReversed: string[];
    pointsSlider: string[];
};

type ThemeFonts = {
    primary: string;
    secondary?: string;
};

type BaseTheme = {
    title: string;
    copyright: string;
    contactLocation: string;
    contactEmail: string;
    socialLinks: {
        facebook: string;
        instagram: string;
        linkedin?: string;
        tiktok?: string;
    };
    breakPoints: typeof BREAKPOINTS;
    widths: typeof WIDTHS;
    colors: ThemeColors;
    gradients: ThemeGradients;
    fonts: ThemeFonts;
    borderRadius: typeof BORDER_RADIUS;
};

// Theme factory
const createTheme = (config: {
    title: string;
    contactEmail: string;
    copyright: string;
    socialLinks: {
        facebook: string;
        instagram: string;
        linkedin?: string;
        tiktok?: string;
    };
    colors: Omit<ThemeColors, keyof typeof SHARED_COLORS>;
    gradients: ThemeGradients;
    fonts: ThemeFonts;
}): BaseTheme => ({
    breakPoints: BREAKPOINTS,
    widths: WIDTHS,
    borderRadius: BORDER_RADIUS,
    contactLocation: "Byron Bay, NSW, 2481, Australia",
    ...config,
    colors: { ...SHARED_COLORS, ...config.colors },
});

// Theme configurations
export const freelanceTheme = createTheme({
    title: "Freelance Travel",
    contactEmail: "support@freelancetravel.com",
    copyright: "Copyright © Freelance Travel. All rights reserved",
    socialLinks: {
        facebook: "https://www.facebook.com/freelancetravelinstantbookingsystem",
        instagram: "https://www.instagram.com/_freelancetravel",
        linkedin: "https://www.linkedin.com/company/freelancetravel",
    },
    colors: {
        main: "#31b4b9",
        main_disable: "#83bdbf",
        label: "#007aff",
        hover: "#e0f7f8",
        button_text: "#ffff",
        button_text1: "#31b4b9",
        bordered_button_text: "#898d8f",
        bordered_button_color: "#31b4b9",
        highlight: "#31b4b9",
        border_chip: "#F2F2F2",
        select: "#37445d",
        slider_text1: "#06908a",
        slider_text2: "#00AEEF",
        time_radio: "#7fbfff",
        points: "#F0EDFF",
        points_text: "#907AFF",
        share_border: "#31b4b9",
        share_background: "#e9f8f9",
        items_background: "#e9f8f9",
        items_color: "#1f929c",
        gray_1: "#333333",
        gray_2: "#4f4f4f",
        gray_5_5: "#e9e9e9",
        red: "#F43F3F",
        black: "#131214",
    },
    gradients: {
        upgradeButton: ["#00EEAB 0%", "#039CE2 100%"],
        button: ["#436CB2 0%", "#28B7C3 100%"],
        buttonReversed: ["#28B7C3 0%", "#436CB2 100%"],
        bannerDiscount: ["#fafdff 0%", "#e6fafd 100%"],
        bannerPointsDesktop: ["#FFFEF9 0%", "#F0EDFF 100%"],
        bannerCommission: ["#FFFEF9 0%", "#F0EDFF 100%"],
        bannerPoints: ["#00EEAB 0%", "#039CE2 100%"],
        primary: ["#436cb2 0%", "#28b7c3 100%"],
        primaryReversed: ["#28b7c3 0%", "#436cb2 100%"],
        pointsSlider: ["#7257FF 0%", "#31b4b9 100%"],
    },
    fonts: {
        primary: "'Poppins', sans-serif",
        secondary: "'Poppins', sans-serif",
    },
});

export const peterpansTheme = createTheme({
    title: "Peterpans Adventure Travel",
    contactEmail: "hello@peterpans.com",
    copyright: "Copyright © Peterpans Adventure Travel. All rights reserved",
    socialLinks: {
        facebook: "https://www.facebook.com/peterpansrocks/",
        instagram: "https://www.instagram.com/peterpanstravel/",
        tiktok: "https://www.tiktok.com/@peterpanstravel",
    },
    colors: {
        main: "#FFD200",
        main_disable: "#FFD85A",
        label: "#131214",
        hover: "#2E5AA9",
        button_text: "#000000",
        button_text1: "#000000",
        bordered_button_text: "#000000",
        bordered_button_color: "#FFD200",
        highlight: "#2E5AA9",
        border_chip: "#FFD200",
        select: "#2E5AA9",
        slider_text1: "#1A1A1A",
        slider_text2: "#2E5AA9",
        time_radio: "#FFD200",
        points: "#FFD200",
        points_text: "#000000",
        share_border: "#FFD200",
        share_background: "#FFFBE6",
        items_background: "#FFFBE6",
        items_color: "#0D0D0D",
        gray_1: "#1A1A1A",
        gray_2: "#4F4F4F",
        gray_5_5: "#E9E9E9",
        red: "#F22F1C",
        black: "#0D0D0D",
        gold: "#FFD700",
        price: "#E31E24",
    },
    gradients: {
        upgradeButton: ["#FFD200", "#FFD200"],
        button: ["#FFD200", "#FFD200"],
        buttonReversed: ["#FFD200", "#FFD200"],
        bannerDiscount: ["#FFFEF9", "#FEFFEF"],
        bannerPoints: ["#FFD200", "#FFD200"],
        bannerCommission: ["#FFFEF9 0%, #F0EDFF 100%"],
        bannerPointsDesktop: ["#FFFEF9 0%", "#F0EDFF 100%"],
        primary: ["#FFD200", "#FFD200"],
        primaryReversed: ["#FFD200", "#FFD200"],
        pointsSlider: ["#7257FF", "#2E5AA9"],
    },
    fonts: {
        primary: "'Poppins', sans-serif",
        secondary: "'Poppins', sans-serif",
    },
});

// Theme selection
type FlavorType = "freelance" | "peterpans";

const getTheme = (): BaseTheme => {
    const flavor = process.env.REACT_APP_FLAVOR as FlavorType;

    if (flavor !== "freelance" && flavor !== "peterpans") {
        console.warn(`[THEME] Unknown flavor '${flavor}', defaulting to 'freelance'`);
        return freelanceTheme;
    }

    return flavor === "freelance" ? freelanceTheme : peterpansTheme;
};

const selectedTheme = getTheme();

// Dynamic CSS injection for MUI components
const injectThemeCSS = (theme: BaseTheme): void => {
    const existingStyle = document.getElementById("dynamic-theme-styles");
    existingStyle?.remove();

    const styleElement = document.createElement("style");
    styleElement.id = "dynamic-theme-styles";
    styleElement.innerHTML = `
        :root {
            --theme-main: ${theme.colors.main};
            --theme-main-disable: ${theme.colors.main_disable};
            --theme-highlight: ${theme.colors.highlight};
            --theme-button_text: ${theme.colors.button_text};
        }
        
        .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
            border-color: var(--theme-main) !important;
        }
        
        .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: var(--theme-main) !important;
            border-width: 2px !important;
        }
        
        .MuiButtonBase-root.MuiPickersDay-root.Mui-selected {
            background-color: var(--theme-main) !important;
            color: var(--theme-button_text) !important;
        }
        
        .MuiButtonBase-root.MuiPickersDay-root.Mui-selected:hover {
            background-color: var(--theme-main) !important;
            color: var(--theme-button_text) !important;
        }
    `;

    document.head.appendChild(styleElement);
};

// Initialize theme CSS
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => injectThemeCSS(selectedTheme));
    } else {
        injectThemeCSS(selectedTheme);
    }
}

// Breakpoint utilities
type CssParams = Parameters<typeof css>;

export const breakpoint = {
    breakSmall: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakSmall}) and (max-width: ${selectedTheme
                .breakPoints.breakMobile}) {
            ${css(...args)}
        }
    `,
    breakMobile: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakMobile}) and (max-width: ${selectedTheme
                .breakPoints.breakMobileMedium}) {
            ${css(...args)}
        }
    `,
    breakMobileMedium: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints
                .breakMobileMedium}) and (max-width: ${selectedTheme.breakPoints.breakTablet}) {
            ${css(...args)}
        }
    `,
    breakTablet: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakTablet}) {
            ${css(...args)}
        }
    `,
    breakMedium: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakMedium}) {
            ${css(...args)}
        }
    `,
    breakIpadPro: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakIpadPro}) {
            ${css(...args)}
        }
    `,
    breakIpad: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakTablet}) and (max-width: ${selectedTheme
                .breakPoints.breakIpadPro}) {
            ${css(...args)}
        }
    `,
    breakLarge: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakLarge}) {
            ${css(...args)}
        }
    `,
    breakBig: (...args: CssParams) => css`
        @media (min-width: ${selectedTheme.breakPoints.breakBig}) {
            ${css(...args)}
        }
    `,
    breakOnlyMobile: (...args: CssParams) => css`
        @media (max-width: ${selectedTheme.breakPoints.breakTablet}) {
            ${css(...args)}
        }
    `,
    breakOnlyMobile768: (...args: CssParams) => css`
        @media (max-width: ${selectedTheme.breakPoints.breakOnlyMobile}) {
            ${css(...args)}
        }
    `,
};

// Exports
export const theme = selectedTheme;

// Theme gradients (e.g. theme.gradients.button) are arrays of color stops.
// Interpolating them directly into styled-components (`background: ${...}`)
// produces invalid CSS and an invisible element — always go through this
// helper to build a valid CSS gradient value.
export const buttonGradient = (stops: readonly string[]): string =>
    `linear-gradient(to right, ${stops.join(", ")})`;
export { BREAKPOINTS, WIDTHS, SHARED_COLORS, BORDER_RADIUS };
export type AppTheme = BaseTheme;
export type FreelanceTheme = typeof freelanceTheme;
export type PeterpansTheme = typeof peterpansTheme;

export default selectedTheme;
